import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { api } from '../../services/api';
import FactureModal from '../../components/feature/FactureModal';
import PaiementModal from '../../components/feature/PaiementModal';
import { EntrepriseSettings, entrepriseSettingsMock } from '../../mocks/entreprise';
import { VenteTissuRecord } from '../../mocks/ventes';
import { formatPrice } from '../../utils/format';
import { useBranding } from '../../context/BrandingContext';

interface UserInfo { id: string; nom: string; role: string; }

interface ProduitStock {
  id: number;
  nom: string;
  type: string;
  couleur: string;
  quantite_stock: number;
  unite: string;
  seuil_alerte: number;
  prix_unitaire: number;
  image_url: string;
}

interface Vente {
  id: number;
  tissu_nom: string;
  quantite: number;
  prix_unitaire: number;
  total: number;
  client_nom: string;
  reste?: number;
  mode_paiement?: string;
}

interface CartItem {
  id: number;
  nom: string;
  unite: string;
  prix_unitaire: number;
  quantite: number;
  remise_item: number;
  image_url: string;
}

export default function VenteTissu() {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [clientNom, setClientNom] = useState('');
  const [remiseGlobale, setRemiseGlobale] = useState<number>(0);
  const [acompte, setAcompte] = useState<number | string>('');
  const [modePaiement, setModePaiement] = useState('Espèces');
  const [dateVente, setDateVente] = useState(''); // New state for custom sale date
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [stock, setStock] = useState<ProduitStock[]>([]);
  const [historique, setHistorique] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { branding } = useBranding();
  const [showFacture, setShowFacture] = useState(false);
  const [factureData, setFactureData] = useState<any>(null);
  const [selectedVenteForPayment, setSelectedVenteForPayment] = useState<any | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    const token = localStorage.getItem('atelier_bassoum_token');
    if (stored) setUser(JSON.parse(stored));
    else navigate('/login');

    // Restore draft
    const draft = localStorage.getItem('atelier_bassoum_vente_draft');
    if (draft) {
      try {
        const d = JSON.parse(draft);
        if (d.cart) setCart(d.cart);
        if (d.clientNom) setClientNom(d.clientNom);
        if (d.remiseGlobale) setRemiseGlobale(d.remiseGlobale);
        if (d.acompte) setAcompte(d.acompte);
        if (d.modePaiement) setModePaiement(d.modePaiement);
        if (d.selectedClientId) setSelectedClientId(d.selectedClientId);
      } catch (e) { console.error("Error restoring draft", e); }
    }

    if (token) {
        refreshData(token);
    }

    // Default sale date is now
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    setDateVente(now.toISOString().slice(0, 16));
  }, [navigate]);

  // Save draft
  useEffect(() => {
    const draft = {
      cart, clientNom, remiseGlobale, acompte, modePaiement, selectedClientId
    };
    localStorage.setItem('atelier_bassoum_vente_draft', JSON.stringify(draft));
  }, [cart, clientNom, remiseGlobale, acompte, modePaiement, selectedClientId]);


  const refreshData = async (token: string) => {
    setLoading(true);
    try {
        const token = localStorage.getItem('atelier_bassoum_token') || '';
        const stocksPromise = api.getStocks(token).catch(e => { console.error("Stock error", e); return []; });
        const ventesPromise = api.getVentesToday(token).catch(e => { console.error("Vente error", e); return []; });
        const clientsPromise = api.getClients(token).catch(e => { console.error("Client error", e); return []; });

        const [stocksData, ventesData, clientsData] = await Promise.all([
            stocksPromise,
            ventesPromise,
            clientsPromise
        ]);
        
        setStock(stocksData || []);
        setHistorique(ventesData || []);
        setClients(clientsData || []);
    } catch (err: any) {
        console.error("Erreur globale refreshData:", err);
        showToast(`Erreur sync: ${err.message || "Inconnue"}`, "error");
    } finally {
        setLoading(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const produitsFiltres = stock.filter(p => {
    const q = search.toLowerCase();
    const nom = (p.nom || "").toLowerCase();
    const type = (p.type || "").toLowerCase();
    return !q || nom.includes(q) || type.includes(q);
  });

  const addToCart = (p: ProduitStock) => {
    const existing = cart.find(item => item.id === p.id);
    if (existing) {
      setCart(cart.map(item => 
        item.id === p.id 
          ? { ...item, quantite: item.quantite + 1 } 
          : item
      ));
    } else {
      setCart([...cart, {
        id: p.id,
        nom: p.nom,
        unite: p.unite,
        prix_unitaire: p.prix_unitaire,
        quantite: 1,
        remise_item: 0,
        image_url: p.image_url
      }]);
    }
    showToast(`${p.nom} ajouté au panier`);
  };

  const updateCartItem = (id: number, field: keyof CartItem, value: any) => {
    setCart(cart.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeFromCart = (id: number) => {
    setCart(cart.filter(item => item.id !== id));
  };

  const totalHT = cart.reduce((acc, item) => acc + (item.prix_unitaire * item.quantite) - item.remise_item, 0);
  const totalNet = Math.max(0, totalHT - remiseGlobale);
  const montantAcompte = acompte === '' ? 0 : Number(acompte);
  const resteAPayer = Math.max(0, totalNet - montantAcompte);

  const handleValider = async () => {
    if (cart.length === 0) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    // Check stock for all items
    for (const item of cart) {
      const p = stock.find(s => s.id === item.id);
      if (p && item.quantite > p.quantite_stock) {
        showToast(`Stock insuffisant pour ${item.nom} !`, 'error');
        return;
      }
    }

    try {
        const dataVente = {
            items: cart.map(item => ({
                tissu_id: item.id,
                quantite: item.quantite,
                prix_unitaire: item.prix_unitaire,
                remise_item: item.remise_item,
                total_ligne: (item.prix_unitaire * item.quantite) - item.remise_item
            })),
            remise_globale: remiseGlobale,
            acompte: montantAcompte,
            reste: resteAPayer,
            client_id: selectedClientId,
            client_nom: clientNom,
            mode_paiement: modePaiement,
            date_vente: dateVente.replace('T', ' ') + ':00'
        };

        const result = await api.createVente(token, dataVente);
        
        showToast(`Vente enregistrée — ${formatPrice(totalNet)}`);
        localStorage.removeItem('atelier_bassoum_vente_draft'); // Clean draft

        // Refresh lists
        refreshData(token);
        
        // Prepare invoice
        const venteFactureData: VenteTissuRecord = {
          id: result.code || `VT-${Date.now()}`,
          clientId: selectedClientId || 'passager',
          clientNom: clientNom || 'Client comptant',
          clientTelephone: clients.find(c => String(c.id) === selectedClientId)?.telephone || '',
          date: dateVente.replace('T', ' ') + ':00',
          articles: cart.map(item => ({
            tissuId: String(item.id),
            tissuNom: item.nom,
            quantite: item.quantite,
            unite: item.unite,
            prixUnitaire: item.prix_unitaire,
            total: (item.prix_unitaire * item.quantite) - item.remise_item
          })),
          total: totalHT,
          remise: remiseGlobale,
          montantRecu: totalNet,
          monnaie: 0,
          acompte: montantAcompte,
          reste: resteAPayer,
          modePaiement,
          vendeur: user?.nom || 'Vendeur'
        };

        setFactureData({ type: 'vente', data: venteFactureData });
        setShowFacture(true);

        // Reset
        setCart([]);
        setRemiseGlobale(0);
        setAcompte('');
        setClientNom('');
        setSelectedClientId(null);
    } catch (err: any) {
        showToast(`Erreur: ${err.message || "Inconnue"}`, "error");
    }
  };

  const handleSolder = async (venteId: number, montantVerse: number, modePaiement: string) => {
    try {
        const token = localStorage.getItem('atelier_bassoum_token') || '';
        await api.updateVentePaiement(token, venteId, { montant_verse: montantVerse, mode_paiement: modePaiement });
        showToast("Paiement partiel enregistré !");
        setSelectedVenteForPayment(null);
        refreshData(token);
    } catch (err: any) {
        showToast(`Erreur: ${err.message || "Inconnue"}`, "error");
    }
  };

  if (!user) return null;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-playfair font-bold text-xl text-gray-900">Vente de tissu</h2>
            <p className="text-xs text-gray-400 mt-0.5">Cliquez sur les tissus pour les ajouter au panier</p>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-playfair font-bold text-base text-gray-900">Catalogue des tissus</h3>
                  <div className="relative max-w-xs w-full">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Rechercher un tissu..."
                      className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-terre-400"
                    />
                  </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-10 animate-pulse text-gray-400">Chargement du stock...</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {produitsFiltres.map(p => {
                      const isLow = Number(p.quantite_stock) <= Number(p.seuil_alerte);
                      const inCart = cart.some(item => item.id === p.id);
                      return (
                        <button
                          key={p.id}
                          onClick={() => addToCart(p)}
                          className={`text-left rounded-xl border-2 overflow-hidden cursor-pointer transition-all hover:shadow-md ${
                            inCart ? 'border-terre-500 bg-terre-50' : 'border-gray-100 bg-white hover:border-gray-200'
                          }`}
                        >
                          <div className="relative h-24">
                             <img src={api.getProxyUrl(p.image_url) || 'https://via.placeholder.com/200'} alt={p.nom} className="w-full h-full object-cover object-top" />
                             {inCart && <div className="absolute top-2 right-2 bg-terre-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white shadow-sm"><i className="ri-check-line" /></div>}
                          </div>
                          <div className="p-3">
                            <p className="text-sm font-bold text-gray-900 truncate leading-tight">{p.nom}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-sm font-black text-terre-600 tracking-tighter">{formatPrice(p.prix_unitaire)}</span>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest ${isLow ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                                {p.quantite_stock} {p.unite}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {historique.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                  <h3 className="font-playfair font-bold text-base text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-history-line text-terre-500" /> Ventes récentes
                  </h3>
                  <div className="space-y-2">
                    {historique.map((v) => (
                      <div key={v.uid} className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 hover:bg-white hover:shadow-sm transition-all group">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 bg-white shadow-sm">
                            {v.tissu_image ? (
                              <img src={api.getProxyUrl(v.tissu_image)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gray-50 flex items-center justify-center text-gray-300"><i className="ri-image-line" /></div>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gray-800 line-clamp-1">{v.tissu_nom}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                                <p className="text-[10px] text-gray-400 uppercase font-bold tracking-tight">{v.client_nom || 'Client comptant'}</p>
                                <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                <time className="text-[10px] text-gray-400 font-bold">{new Date(v.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</time>
                            </div>
                          </div>
                        </div>
                        <div className="text-right flex flex-col items-end">
                          <p className="text-sm font-black text-gray-900 tracking-tighter">{formatPrice(v.total)}</p>
                          {v.reste > 0 ? (
                            <button 
                              onClick={() => setSelectedVenteForPayment(v)}
                              className="mt-1 text-[9px] bg-red-50 text-red-600 font-bold px-2 py-0.5 rounded-full border border-red-100 hover:bg-red-500 hover:text-white transition-all cursor-pointer"
                            >
                              Reste : {formatPrice(v.reste)}
                            </button>
                          ) : (
                            <span className="text-[10px] text-green-500 font-bold flex items-center gap-1">
                              <i className="ri-checkbox-circle-fill" /> Payé
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="xl:col-span-1">
              <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-6 shadow-sm">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-50">
                  <h3 className="font-playfair font-bold text-base text-gray-900">Panier d'achat</h3>
                  <span className="bg-terre-100 text-terre-600 text-xs font-bold px-2 py-0.5 rounded-full">{cart.length} articles</span>
                </div>

                {cart.length === 0 ? (
                  <div className="text-center py-16 text-gray-300">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-100">
                      <i className="ri-shopping-bag-line text-2xl" />
                    </div>
                    <p className="text-sm font-medium">Le panier est encore vide</p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="max-h-[350px] overflow-y-auto pr-1 -mr-1 space-y-3">
                      {cart.map(item => (
                        <div key={item.id} className="bg-gray-50/50 rounded-xl p-3 border border-gray-100 group relative">
                          <button 
                            onClick={() => removeFromCart(item.id)}
                            title="Retirer cet article"
                            className="absolute -top-2 -right-2 w-6 h-6 bg-white shadow-sm border border-red-100 text-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <i className="ri-close-line" />
                          </button>
                          <div className="flex gap-3 mb-3">
                            <img src={api.getProxyUrl(item.image_url)} alt="" className="w-10 h-10 rounded-lg object-cover shadow-sm" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-gray-900 truncate">{item.nom}</p>
                                <p className="text-[10px] text-gray-400 font-bold uppercase">{formatPrice(item.prix_unitaire)} / {item.unite}</p>
                            </div>
                            <p className="text-sm font-black text-gray-900 tracking-tighter">{formatPrice(item.prix_unitaire * item.quantite - item.remise_item)}</p>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                               <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1 block">Quantité ({item.unite})</label>
                               <input 
                                 type="number" 
                                 value={item.quantite}
                                 onChange={e => updateCartItem(item.id, 'quantite', parseFloat(e.target.value) || 0)}
                                 title={`Quantité pour ${item.nom}`}
                                 className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-bold focus:ring-2 focus:ring-terre-400 outline-none" 
                               />
                            </div>
                            <div>
                               <label className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1 block">Remise (/art)</label>
                               <input 
                                 type="number" 
                                 value={item.remise_item}
                                 onChange={e => updateCartItem(item.id, 'remise_item', parseInt(e.target.value) || 0)}
                                 placeholder="0"
                                 title={`Remise par article pour ${item.nom}`}
                                 className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-bold text-gold-600 focus:ring-2 focus:ring-gold-400 outline-none" 
                               />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="space-y-3 pt-4 border-t border-gray-50">
                      <div>
                        <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Client</label>
                        <select 
                          value={selectedClientId || ''} 
                          title="Sélectionner un client"
                          onChange={e => {
                            const val = e.target.value;
                            setSelectedClientId(val || null);
                            if (val) {
                              const c = clients.find(cl => String(cl.id) === val);
                              setClientNom(c?.nom || '');
                            } else {
                              setClientNom('');
                            }
                          }}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terre-400 font-bold mb-2"
                        >
                          <option value="">Client comptant (Passager)</option>
                          {clients.map(c => (
                            <option key={c.id} value={c.id}>{c.nom} ({c.telephone})</option>
                          ))}
                        </select>
                        {!selectedClientId && (
                          <div className="space-y-2">
                            <input 
                              type="text" 
                              value={clientNom} 
                              onChange={e => setClientNom(e.target.value)} 
                              placeholder="Nom du client passager"
                              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terre-400" 
                            />
                            {clientNom && clients.some(c => c.nom.toLowerCase() === clientNom.toLowerCase()) && (
                              <div className="bg-gold-50 border border-gold-200 rounded-lg p-2 flex items-center justify-between">
                                <span className="text-[10px] text-gold-700 font-bold">Un client porte déjà ce nom.</span>
                                <button 
                                  onClick={() => {
                                    const match = clients.find(c => c.nom.toLowerCase() === clientNom.toLowerCase());
                                    if (match) {
                                      setSelectedClientId(String(match.id));
                                      setClientNom(match.nom);
                                    }
                                  }}
                                  className="text-[10px] bg-gold-500 text-white px-2 py-1 rounded font-black uppercase"
                                >
                                  Lier
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 mb-1.5 uppercase">Remise Facture</label>
                          <input 
                            type="number" 
                            value={remiseGlobale || ''} 
                            onChange={e => setRemiseGlobale(parseInt(e.target.value) || 0)} 
                            placeholder="0"
                            title="Remise sur facture"
                            className="w-full border border-gold-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-gold-400 bg-gold-50/20 font-black text-gold-700 outline-none" 
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 mb-1.5 uppercase">Acompte</label>
                          <input 
                            type="number" 
                            value={acompte} 
                            onChange={e => setAcompte(e.target.value)} 
                            placeholder={formatPrice(totalNet)}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-terre-400 bg-gray-50 font-black text-gray-700 outline-none" 
                          />
                        </div>
                      </div>

                      <div>
                         <label htmlFor="date-vente-input" className="block text-[10px] font-bold text-gray-400 mb-1.5 uppercase">Date de la vente</label>
                         <div className="relative">
                            <i className="ri-calendar-line absolute left-3 top-1/2 -translate-y-1/2 text-terre-500 z-10" />
                            <input 
                              id="date-vente-input"
                              type="datetime-local" 
                              value={dateVente} 
                              onChange={e => setDateVente(e.target.value)}
                              title="Date de la vente"
                              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-terre-400 bg-gray-50 font-bold outline-none" 
                            />
                         </div>
                      </div>

                      <div>
                         <label className="block text-[10px] font-bold text-gray-400 mb-1.5 uppercase">Mode de Paiement</label>
                         <div className="grid grid-cols-2 gap-2">
                           {['Espèces', 'Mobile Money'].map(m => (
                             <button type="button" key={m} onClick={() => setModePaiement(m)}
                               className={`py-2 rounded-lg text-xs font-bold border transition-all ${modePaiement === m ? 'bg-terre-500 text-white border-terre-500 shadow-sm' : 'text-gray-600 border-gray-100 bg-white hover:bg-gray-50'}`}>
                               {m}
                             </button>
                           ))}
                         </div>
                      </div>

                      <div className="bg-terre-50 rounded-2xl p-4 border border-terre-100 mt-4">
                         <div className="flex justify-between items-center mb-1">
                           <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">Sous-total</span>
                           <span className="font-bold text-gray-900">{formatPrice(totalHT)}</span>
                         </div>
                         <div className="flex justify-between items-center pt-2 border-t border-terre-100">
                           <span className="font-black text-gray-900 uppercase text-xs tracking-widest">Total Net</span>
                           <span className="font-black text-terre-700 text-2xl tracking-tighter">{formatPrice(totalNet)}</span>
                         </div>
                         {resteAPayer > 0 && (
                            <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed border-terre-200">
                              <span className="text-[10px] font-bold text-red-600 uppercase">Reste :</span>
                              <span className="text-sm font-black text-red-600">{formatPrice(resteAPayer)}</span>
                            </div>
                         )}
                      </div>

                      <button 
                        onClick={handleValider}
                        disabled={cart.length === 0}
                        className={`w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all shadow-lg active:scale-95 ${
                          cart.length > 0 
                            ? 'bg-terre-500 hover:bg-terre-600 text-white shadow-terre-200 border-b-4 border-terre-700' 
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Valider & Facturer
                      </button>
                    </div>
                  </div>
                )}
               </div>
            </div>
          </div>
        </main>
      </div>

      {showFacture && factureData && (
        <FactureModal 
          entreprise={branding} 
          factureData={factureData} 
          onClose={() => setShowFacture(false)} 
        />
      )}

      {selectedVenteForPayment && selectedVenteForPayment.reste !== undefined && (
        <PaiementModal
          reste={selectedVenteForPayment.reste}
          onValider={(montant, mode) => handleSolder(selectedVenteForPayment.id, montant, mode)}
          onClose={() => setSelectedVenteForPayment(null)}
        />
      )}
    </div>
  );
}

